/**
 * One payment request: what it asks for, what came in, and what staff can do
 * about it.
 *
 * Every figure here comes from `requestDetail`, so the drawer cannot disagree
 * with the row that opened it. Credit note and Cancel are confirmed inside the
 * drawer rather than in a modal on top of it — both are irreversible, and a
 * second overlay over a drawer hides the balance the decision depends on.
 *
 * Spec: DESIGN-BRIEF.md §E (request detail drawer) and `vReqDrawer()`.
 */
import React, {useState} from 'react';
import {money, type NetlyPayData, type PaymentRequest} from '../../../internals/managers/NetlyPayManager';
import {creditNoteError, requestDetail, type TimelineTone} from '../requests';
import {Drawer, GhostButton, PrimaryButton} from '../components/Overlays';
import {RequestStatusChip} from '../components/StatusChip';

const TONE_DOT: Record<TimelineTone, {bg: string; border: string; text: string}> = {
  done: {bg: 'bg-text3/60', border: 'border-transparent', text: 'text-text1'},
  ok: {bg: 'bg-pos', border: 'border-transparent', text: 'text-pos'},
  warn: {bg: 'bg-warn', border: 'border-transparent', text: 'text-text1'},
  accent: {bg: 'bg-brand', border: 'border-transparent', text: 'text-text1'},
  bad: {bg: 'bg-neg', border: 'border-transparent', text: 'text-neg'},
  next: {bg: 'bg-surface', border: 'border-text3/60', text: 'text-text3'},
};

const label = 'text-[11px] font-semibold tracking-[.06em] text-text3';
const inputCls =
  'w-full h-10 px-3 rounded-lg border border-borderc2 bg-surface text-[13px] text-text1 outline-none focus:border-brand transition-colors';

export const RequestDrawer = ({
  data,
  request,
  currency = 'Rs.',
  busy,
  onClose,
  onRemind,
  onReview,
  onReceipt,
  onCreditNote,
  onCancel,
}: {
  data: NetlyPayData;
  request: PaymentRequest;
  currency?: string;
  busy?: boolean;
  onClose: () => void;
  onRemind: () => void;
  onReview: (approvalId: string) => void;
  onReceipt: () => void;
  onCreditNote: (amountMinor: number, reason: string) => void;
  onCancel: (reason: string) => void;
}) => {
  const d = requestDetail(data, request, undefined, currency);
  const [pane, setPane] = useState<'none' | 'credit' | 'cancel'>('none');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const amountMinor = Math.round(Number(amount) * 100);
  const creditError = amount.trim() ? creditNoteError(amountMinor, d.creditMaxMinor, currency) : null;
  const canSaveCredit = !!amount.trim() && !creditError && !!reason.trim();

  return (
    <Drawer title={d.title} sub={`${d.id} · ${d.who}`} width={560} onClose={onClose}>
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between gap-3">
          <RequestStatusChip status={d.status} />
          <span className="text-[12px] text-text3">{d.terms}</span>
        </div>

        {/* Amount, paid, balance */}
        <div className="grid grid-cols-3 gap-3">
          {d.sums.map((s) => (
            <div key={s.label} className="rounded-xl border border-borderc bg-surface2 px-3.5 py-3">
              <p className={label}>{s.label}</p>
              <p
                className={`font-mono tabular-nums font-semibold mt-1.5 text-[clamp(15px,1.4vw,19px)] ${
                  s.tone === 'danger' ? 'text-neg' : s.tone === 'pos' ? 'text-pos' : 'text-text1'
                }`}
              >
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* Line items */}
        <section>
          <h3 className="text-[13px] font-semibold text-text1 mb-2">What this asks for</h3>
          <div className="rounded-xl border border-borderc overflow-hidden">
            {d.items.map((i, ix) => (
              <div
                key={`${i.label}-${ix}`}
                className="flex items-center justify-between gap-4 px-3.5 py-2.5 border-b border-borderc last:border-b-0"
              >
                <span className={`text-[13px] ${i.isCredit ? 'text-pos' : 'text-text1'}`}>{i.label}</span>
                <span
                  className={`font-mono tabular-nums text-[13px] ${
                    i.isCredit ? 'text-pos' : 'text-text1'
                  }`}
                >
                  {i.amount}
                </span>
              </div>
            ))}
            <div className="flex items-center justify-between gap-4 px-3.5 py-2.5 bg-surface2">
              <span className="text-[13px] font-semibold text-text1">Total</span>
              <span className="font-mono tabular-nums text-[13px] font-semibold text-text1">{d.total}</span>
            </div>
          </div>
        </section>

        {/* Payments */}
        {d.payments.length > 0 && (
          <section>
            <h3 className="text-[13px] font-semibold text-text1 mb-2">Payments</h3>
            <div className="rounded-xl border border-borderc overflow-hidden">
              {d.payments.map((p, ix) => (
                <div
                  key={`${p.date}-${ix}`}
                  className="flex items-center justify-between gap-3 px-3.5 py-2.5 border-b border-borderc last:border-b-0"
                >
                  <span className="min-w-0">
                    <span className="block text-[13px] text-text1">{p.method}</span>
                    <span className="block text-[11.5px] font-mono text-text3 truncate">
                      {[p.date, p.ref].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  <span className="font-mono tabular-nums text-[13px] text-pos">{p.amount}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Timeline */}
        <section>
          <h3 className="text-[13px] font-semibold text-text1 mb-2">History</h3>
          <ol className="relative pl-4">
            <span className="absolute left-[3px] top-1.5 bottom-1.5 w-px bg-borderc" aria-hidden="true" />
            {d.timeline.map((e, ix) => {
              const tone = TONE_DOT[e.tone];
              return (
                <li key={`${e.at}-${ix}`} className="relative py-1.5">
                  <span
                    className={`absolute -left-4 top-2.5 w-[7px] h-[7px] rounded-full border ${tone.bg} ${tone.border}`}
                    aria-hidden="true"
                  />
                  <p className={`text-[13px] ${tone.text}`}>{e.text}</p>
                  <p className="text-[11.5px] font-mono text-text3">{e.when}</p>
                </li>
              );
            })}
          </ol>
        </section>

        {/* Credit note */}
        {pane === 'credit' && (
          <section className="rounded-xl border border-brand/40 bg-brand-soft/30 p-4">
            <h3 className="text-[13px] font-semibold text-text1">Issue a credit note</h3>
            <p className="text-[12.5px] text-text2 mt-1">
              This lowers what the member owes without any money moving. The balance is{' '}
              {money(d.creditMaxMinor, currency)}.
            </p>
            <div className="mt-3 flex flex-col gap-2.5">
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                placeholder={`Amount in ${currency}`}
                aria-label="Credit note amount"
                className={`${inputCls} font-mono ${creditError ? 'border-neg' : ''}`}
              />
              {creditError && <p className="text-[12px] text-neg">{creditError}</p>}
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason, for the member and the ledger"
                aria-label="Credit note reason"
                className={inputCls}
              />
              <div className="flex items-center gap-2.5">
                <PrimaryButton
                  disabled={!canSaveCredit || busy}
                  onClick={() => onCreditNote(amountMinor, reason.trim())}
                >
                  Issue credit note
                </PrimaryButton>
                <GhostButton onClick={() => setPane('none')}>Back</GhostButton>
              </div>
            </div>
          </section>
        )}

        {/* Cancel */}
        {pane === 'cancel' && (
          <section className="rounded-xl border border-neg/40 bg-neg-soft/40 p-4">
            <h3 className="text-[13px] font-semibold text-text1">Cancel {d.id}?</h3>
            <p className="text-[12.5px] text-text2 mt-1">
              The member stops being asked for it and any proof waiting on it is withdrawn. The request
              stays on their history.
            </p>
            <div className="mt-3 flex flex-col gap-2.5">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (optional)"
                aria-label="Cancellation reason"
                className={inputCls}
              />
              <div className="flex items-center gap-2.5">
                <PrimaryButton tone="danger" disabled={busy} onClick={() => onCancel(reason.trim())}>
                  Cancel request
                </PrimaryButton>
                <GhostButton onClick={() => setPane('none')}>Keep it</GhostButton>
              </div>
            </div>
          </section>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2.5 flex-wrap pt-1">
          {d.canRemind && (
            <PrimaryButton disabled={busy} onClick={onRemind}>
              Remind
            </PrimaryButton>
          )}
          {d.canReview && d.approvalId && (
            <PrimaryButton onClick={() => onReview(d.approvalId as string)}>Review proof</PrimaryButton>
          )}
          {d.status === 'paid' && <GhostButton onClick={onReceipt}>Download receipt</GhostButton>}
          {d.canCredit && (
            // Disabled on purpose: the Pay API has no credit-note route yet,
            // so the pane behind this could take an amount and a reason and
            // then fail on save. An inert control that explains itself beats
            // one that looks like it worked.
            <GhostButton
              disabled
              title="Credit notes are not available yet"
              onClick={() => {
                setPane('credit');
                setAmount('');
                setReason('');
              }}
            >
              Credit note
            </GhostButton>
          )}
          {d.canCancel && (
            <button
              type="button"
              onClick={() => {
                setPane('cancel');
                setReason('');
              }}
              className="h-10 px-4 rounded-[9px] text-[13.5px] font-medium text-neg hover:bg-neg-soft transition-colors"
            >
              Cancel request
            </button>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default RequestDrawer;
